import type React from 'react';
import { useState } from 'react';
import type { PartnerApiRow, PartnerListQuery } from '@/api/partners';
import { AddPartnerModal } from '@/components/partners/add-partner-modal';
import { DeletePartnerDialog } from '@/components/partners/delete-partner-dialog';
import { EditPartnerModal } from '@/components/partners/edit-partner-modal';
import { PartnersTable } from '@/components/partners/partners-table';
import { useFocusReturn } from '@/hooks/use-focus-return';
import { usePartners } from '@/hooks/use-partners';

export const Partners: React.FC = () => {
  const {
    partners,
    pagination,
    isLoading,
    error,
    filters,
    setSearch,
    setStatus,
    setPage,
    setSort,
    refetch,
  } = usePartners();

  const [addOpen, setAddOpen] = useState(false);
  const [editPartner, setEditPartner] = useState<PartnerApiRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PartnerApiRow | null>(null);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();

  const handleFilterChange = (filterKey: string, value: string | string[]) => {
    if (filterKey === 'status') {
      const v = Array.isArray(value) ? value[0] : value;
      setStatus(v === '' || v === undefined ? undefined : (v as PartnerListQuery['status']));
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <h1 className="text-2xl font-bold tracking-tight">Partners</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage partner entities — resellers, agents, integration partners, and partnerships.
        </p>
      </div>

      <div className="px-4 lg:px-6">
        <PartnersTable
          data={{ data: partners, ...(pagination ? { pagination } : {}) }}
          isLoading={isLoading}
          error={error}
          currentFilterValues={{ status: filters.status ?? '' }}
          currentSortKey={filters.sortBy ?? 'partner_name'}
          currentSortOrder={filters.order ?? 'asc'}
          searchValue={filters.search ?? ''}
          onFilterChange={handleFilterChange}
          onSortChange={setSort}
          onPageChange={setPage}
          onSearchChange={setSearch}
          onAdd={() => {
            captureFocus();
            setAddOpen(true);
          }}
          onEdit={(p) => {
            captureFocus();
            setEditPartner(p);
          }}
          onDelete={(p) => {
            captureFocus();
            setDeleteTarget(p);
          }}
          getRowLink={(p) => ({ to: `/partners/${p.party_id}` })}
        />
      </div>

      <AddPartnerModal
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) restoreFocus();
          setAddOpen(open);
        }}
        onSuccess={refetch}
      />
      <EditPartnerModal
        open={editPartner !== null}
        partner={editPartner}
        onOpenChange={(open) => {
          if (!open) {
            restoreFocus();
            setEditPartner(null);
          }
        }}
        onSuccess={refetch}
      />
      <DeletePartnerDialog
        open={deleteTarget !== null}
        partner={deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            restoreFocus();
            setDeleteTarget(null);
          }
        }}
        onSuccess={refetch}
      />
    </div>
  );
};
